import { withTranslation } from "react-i18next";
import WithRouter from "../../../../components/Common/WithRouter";
import React, { useRef, useState } from "react";
import { request } from "../../../../utils/Request";
import Tooltip from "../../../../components/Common/Display/Tooltip";
import HasAccess from "../../../../utils/HasAccess";
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import StateLessGrid from "../../../../components/Shared/StateLessGrid";
import QuickCopyDropdown from "../../../../components/Shared/QuickCopyDropdown";
import { Button } from "primereact/button";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import { InputField } from "../../../../components/Shared/InputField";
import TravelOrderForm from "./TravelOrderForm";
import TravelOrderFilter from "./TravelOrderFilter";
import { utcDateToLocalDate } from "../../../../utils/Helper";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { includes, isEmpty, get } from "lodash";
import TravelOrderDetail from "./TravelOrderDetail";
import { ButtonGroup } from "primereact/buttongroup";

const TravelOrderList = (props: any) => {
    const { t, moduleState, toastify } = props;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [operation, setOperation] = useState<any>(searchParams.get('operation') ?? null);
    const [operationId, setOperationId] = useState<any>(searchParams.get('operationId') ?? null);
    const [filterPanel, setFilterPanel] = useState<boolean>(false);
    const [initSearchFormState, setInitSearchFormState] = useState<any>({ search: moduleState.search ?? null });
    const tableRef: any = useRef();
    const [total, setTotal] = useState(0);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [extra, setExtra] = useState<any>([]);

    const onDelete = async (payloads: any): Promise<void> => {
        try {
            await request({
                url: `suppliers/travel/orders/${ payloads.id }`,
                method: 'DELETE',
                data: { _method: 'DELETE' }
            });

            toastify(t("record deleted", { item: t('travel order') }), "info");
            onReset();
        } catch (e: any) {
            toastify(t("server error"), "error");
        }
    };

    const columns = [
        {
            field: 'travel_order_number',
            title: t('travel order number'),
            sorting: true,
            render: (_row: any) => <span className={ "text-capitalize" }>{ _row.travel_order_number }</span>
        },
        {
            field: 'departure_date',
            title: t('departure date'),
            sorting: true,
            render: ({ departure_date }: any) => {
                return (
                    <>
                        { ( departure_date ) && <span>{ utcDateToLocalDate(departure_date) }</span> }
                    </>
                );
            },
        },
        {
            field: 'return_date',
            title: t('return date'),
            sorting: true,
            render: ({ return_date }: any) => {
                return (
                    <>
                        { ( return_date ) && <span>{ utcDateToLocalDate(return_date) }</span> }
                    </>
                );
            },
        },
        {
            field: 'transport_vehicle_id',
            title: t('transport vehicle'),
            sorting: true,
            render: ({ transport_vehicle }: any) => <span className={ "text-capitalize" }>
                { get(transport_vehicle, ['truck_plate_number']) } | { get(transport_vehicle, ['trailer_plate_number']) }
            </span>
        },
        {
            field: 'legal_entity_id',
            title: t('legal entity'),
            sorting: true,
            render: ({ legal_entity }: any) => <span className={ "text-capitalize" }>
                { get(legal_entity, ['company_name']) }
            </span>
        },
        {
            field: 'start_mileage',
            title: t('Mileage'),
            sorting: true,
            render: ({ start_mileage, end_mileage }: any) => <span className={ "text-capitalize" }>
                { start_mileage } / { end_mileage }
            </span>
        },
        {
            field: 'total_kilometers',
            title: t('total kilometers'),
            sorting: true,
            render: ({ total_kilometers }: any) => <span className={ "text-capitalize" }>
                { total_kilometers }
            </span>
        },
        {
            field: 'revenue',
            title: t('revenue'),
            sorting: true,
        },
        {
            field: 'coefficient',
            title: t('coefficient'),
            sorting: true,
        },
        {
            field: 'created_at',
            title: t('created at'),
            sorting: true,
            render: ({ created_at }: any) => {
                return (
                    <>
                        { ( created_at ) && <span>{ utcDateToLocalDate(created_at) }</span> }
                    </>
                );
            },
        },
        {
            field: 'action',
            title: t('action'),
            sorting: false,
            render: (_row: any) => {
                return (
                    <>
                        <HasAccess hasAllAccess={ ['travelordersupdate'] }>
                            <Tooltip
                                tooltip={ t('edit item') }>
                                <a onClick={ () => onOperationClick('edit', _row.id) }>
                                    <i className="fas fa-edit" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                        <HasAccess hasAllAccess={ ['travelordersdestroy'] }>
                            <Tooltip
                                tooltip={ t('delete item') }>
                                <a
                                    title={ t('delete item') }
                                    className={ "cursor-pointer ms-2 btn btn-soft-danger waves-effect waves-light btn-xs" }
                                    onClick={ (event: any) => confirmPopup({
                                        target: event.currentTarget,
                                        message: t('do you want to delete this record?'),
                                        icon: 'pi pi-info-circle',
                                        // @ts-ignorer
                                        defaultFocus: 'reject',
                                        acceptClassName: 'p-button-danger',
                                        accept: () => onDelete(_row),
                                        reject: () => {
                                        },
                                    }) }>
                                    <i className="fas fa-trash-alt" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                        <HasAccess hasAllAccess={ ['travelordersview'] }>
                            <Tooltip tooltip={ t('detail of item') }>
                                <a
                                    className={ "cursor-pointer ms-2" }
                                    onClick={ () => onOperationClick('detail', _row.id) }>
                                    <i className="fas fa-eye" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                    </>
                );
            }
        },
    ];

    const onOperationClick = (operationToBePerformed: any = 'add', id: any = null) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('operation', operationToBePerformed);
        searchParams.set('operationId', id);

        if ( isEmpty(operationToBePerformed) ) {
            searchParams.delete('operation');
        }

        if ( isEmpty(id) ) {
            searchParams.delete('operationId');
        }

        navigate({
            pathname: location.pathname,
            search: searchParams.toString(),
        }, { replace: true });
        setOperationId(id);
        setOperation(operationToBePerformed);
    };

    const onCloseOperation = (reset: any = true) => {
        onOperationClick(null, null);
        onReset(reset);
        focusOnSearch();
    };

    const onContentLoaded = (response: any) => setExtra(response.extra);

    const onReset = (reset: any = true) => {
        const mStates = { ...moduleState };

        if ( reset ) {
            mStates.filters = [];
            mStates.page = 1;
            mStates.search = null;
        }

        props.setQuery(mStates);
    };

    const onSearch = ({ search }: any) => {
        props.setQuery({
            ...moduleState,
            search: search
        });
    };

    const onFilterClick = () => {
        setFilterPanel(true);
    };

    const focusOnSearch = () => {
        const searchInput: HTMLInputElement | null = document.querySelector('input[name="search"]');
        if ( searchInput ) {
            searchInput.focus();
        }
    };

    return (
        <div className={ "block-viewer" }>
            <div className="block-section">
                <div className="block-header">
                    <div className="block-title">
                        <div>
                            <h5 className={ "mb-0" }>{ t('travel orders') } ({total})</h5>
                            <div>
                                <Row className={ "" }>
                                    {
                                        extra && <>
                                          <div>
                                              <span>{t('total kilometers')}: </span> {extra.total_kilometers}
                                              <span className={"ml-2"}>{t('total revenue')}: </span> {extra.total_revenue}
                                              <span className={"ml-2"}>{t('coefficient')}: </span> {extra.coefficient}
                                          </div>
                                      </>
                                    }
                                </Row>
                            </div>
                        </div>
                    </div>
                    <div className="block-actions">
                        {
                            operation == 'detail' &&
                          <div>
                              <Button type={ "button" }
                                      tooltip={ t("operation cancel") } className={ "ms-2" }
                                      size={ "small" } severity={ "warning" }
                                      tooltipOptions={ { position: 'top' } } onClick={ () => onCloseOperation(false) }>
                                  <i className="pi pi-arrow-left me-2" /> { t("cancel") }

                              </Button>
                          </div>
                        }
                    </div>
                </div>
                <div className="block-content">
                    <Row className={ `p-2 ${operation === 'detail' ? 'position-minus' : ''}` }>
                        <Col
                            sm={ 12 } md={ 6 } className={ "mb-2" }>
                            <Formik
                                enableReinitialize={ true }
                                initialValues={ initSearchFormState }
                                onSubmit={ onSearch }
                            >
                                { (props: FormikProps<any>) => {
                                    return (
                                        <Form onSubmit={ props.handleSubmit }>
                                            <Col sm={ 12 } md={ 8 }>
                                            <Field component={ InputField } withoutLabel={ true }
                                                       autoFocus={ true }
                                                       name="search"
                                                       formGroupClass={ "mb-0" }
                                                       placeholder={ t("search") } />
                                            </Col>
                                        </Form>
                                    );
                                } }
                            </Formik>
                        </Col>

                        <Col
                            sm={ 12 } md={ 6 } className={ "text-right mb-2" }>
                                <div className="d-inline-block me-2 align-middle text-left">
                                    <QuickCopyDropdown
                                        columns={ columns }
                                        tableName={ "travelOrderList" }
                                        selectedRows={ selectedRows }
                                        trackBy={ "id" }
                                        // @ts-ignore
                                        entities={ tableRef.current?.getEntities?.() || [] }
                                    />
                                </div>
                                <ButtonGroup>
                                    <Button size={ "small" } outlined tooltip={ t('add item') }
                                            tooltipOptions={ { position: 'top' } }
                                            onClick={ () => onOperationClick('add') }>
                                        <i className="pi pi-plus" />
                                        <span className={ "ms-1" }>{ t('add') }</span>
                                    </Button>
                                    <Button icon="pi pi-filter-fill" size={ "small" } outlined tooltip={ t('filter') }
                                            onClick={ onFilterClick }
                                            tooltipOptions={ { position: 'top' } } />
                                    <Button onClick={ onReset } icon="pi pi-refresh" size={ "small" } outlined
                                            tooltip={ t('reset') }
                                            tooltipOptions={ { position: 'top' } } />
                                </ButtonGroup>
                        </Col>

                        <Col sm={ 12 }>
                            <StateLessGrid
                                size={ "normal" }
                                url={ '/suppliers/travel/orders?1=1' }
                                name={ "travelOrderList" }
                                columns={ columns }
                                moduleState={ moduleState }
                                setQuery={ props.setQuery }
                                ref={ tableRef }
                                itemSize={ 45 }
                                getMetaData={ onContentLoaded }
                                setTotal={ setTotal }
                                stickyHeader={ true }
                                showQuickCopy={ false }
                                onRowSelection={ setSelectedRows }
                                selectedRows={ selectedRows }
                                // detailRow={ (data: any) => <DetailRow data={ data } /> }
                            />
                        </Col>
                    </Row>

                    {
                        (operation === 'detail' && !isEmpty(operationId)) &&
                      <>
                          <TravelOrderDetail
                            setQuery={ props.setQuery }
                            toastify={ props.toastify }
                            dropdowns={ props.dropdowns }
                            params={ { operation, operationId } }
                            onClose={ onCloseOperation }
                          />
                      </>
                    }

                    {
                        (includes(['add', 'edit'], operation)) &&
                        <TravelOrderForm
                            moduleState={ moduleState }
                            setQuery={ props.setQuery }
                            toastify={ props.toastify }
                            dropdowns={ props.dropdowns }
                            params={ { operation, operationId } }
                            onClose={ onCloseOperation }
                        />
                    }

                    <TravelOrderFilter
                        moduleState={ moduleState }
                        setQuery={ props.setQuery }
                        toastify={ props.toastify }
                        dropdowns={ props.dropdowns }
                        visibility={ filterPanel }
                        onClose={ () => {
                            setFilterPanel(false);
                            focusOnSearch();
                        } }
                    />

                    <ConfirmPopup />
                </div>
            </div>
        </div>
    );
};

TravelOrderList.propTypes = {
    // t: PropTypes.any,
};

export default withTranslation()(WithRouter(TravelOrderList));
